import { AnyEnum } from '@naturalcycles/js-lib'
import {
  BirthControlMethodBreakpointOption,
  BleedingChangesOption,
  CervicalMucusChangesOption,
  CycleChangesBreakpointOption,
  EligibleAppleWatchBreakpointOption,
  EmotionsOption,
  Experiment,
  getBucket,
  Goal,
  GoalBreakpointOption,
  HormonalBirthControlHowLongBreakpointOption,
  HormonalBirthControlUsageBreakpointOption,
  LibidoChangesOption,
  PartnerInvolvementOption,
  QuizDataInput,
  QuizDataKey,
  QuizGuardData,
  SkinChangesOption,
  TypeOfPainOption,
} from '@naturalcycles/shared'
import { Page, ParentPage } from '@src/app/shared/typings/enum/pages.js'
import {
  BirthControlMethodEfficacy,
  InfoPageContent,
} from '@src/app/shared/typings/interfaces/quiz.js'
import { getSignalStore, getState } from '../core/store/signalStore.js'
import { flowUtils } from '../core/util/flow.util.js'
import { Sections } from '../shared/typings/enum/quiz.js'

/**
 * Between these two set of pages below, there are more pages, decided by breakpoint FlowBreakpoint.cycleChanges.
 * Start and end part are joined by default as it is the flow when the user does not select any cycle changes on FlowBreakpoint.cycleChanges.
 * More pages are injected in between if user selects cycle changes on FlowBreakpoint.cycleChanges
 */
export const sectionCycleChangesStart = [Page.emotionalChanges, Page.cycleChanges]
export const sectionCycleChangesEnd = [Page.trackingInfo]
export const sectionRoutine = [
  Page.naturalMethod,
  Page.yourMeasuringDevice,
  Page.whichWearable,
  Page.sleepWearable,
  Page.resultOverview,
]

export const sectionSexQuestions = [
  Page.learnAboutSex,
  Page.bestTimeInCycle,
  Page.knowingYourSexDrive,
  Page.sexDriveInfo,
  Page.sexFrequency,
  Page.knowPregnantDays,
  Page.menstrualCycleInfo,
  Page.testimonialInfo,
]

export const sectionCyclePeriodTrackers = [
  Page.periodTrackerUsage,
  Page.whichPeriodTracker,
  Page.periodTrackerComparison,
]

export const sectionCycleEnd = [
  Page.doctorInvolvement,
  Page.partnerInvolvement,
  Page.partnerInvolvementInfo,
  Page.followPregnancyInfo,
]

export const commonQuizFlow = [
  [Page.fertilityGoal, Page.privacy, Page.name, Page.usps, Page.birthday],
  [],
  [],
  [],
  [],
]

export const clearblueQuizFlow = [
  [Page.fertilityGoal, Page.privacy, Page.name, Page.usps, Page.birthday],
  [],
  [],
  [],
]

export const quizParentSlug = ParentPage.quiz

export const allQuizSections: Sections = {
  intro: 0,
  goal: 1,
  cycle: 2,
  cycleChanges: 3,
  routine: 4,
}

export const baseImgLink = 'https://assets.naturalcycles.com/images/onboarding-quiz/'

export type FertilityGoalInfoPageContent = InfoPageContent & {
  backgroundColor: string
  buttonColor: string
  buttonTextColor: string
  backButtonColor: string
  logoColor: string
  referenceKey?: string
}

const yesContentData: InfoPageContent = {
  imageUrl: 'assets/img/img-PartnerInvolvementOption--true.png',
  titleKey: 'txt-PartnerInvolvementOption--true',
  buttonKey: 'txt-next',
}

const noContentData: InfoPageContent = {
  imageUrl: 'assets/img/img-PartnerInvolvementOption--false.png',
  titleKey: 'txt-PartnerInvolvementOption--false',
  buttonKey: 'txt-next',
}

export const partnerInvolvementInfoContent: Record<PartnerInvolvementOption, InfoPageContent> = {
  [PartnerInvolvementOption.noPartner]: noContentData,
  [PartnerInvolvementOption.notInvolved]: noContentData,
  [PartnerInvolvementOption.someWhatInvolved]: yesContentData,
  [PartnerInvolvementOption.veryInvolved]: yesContentData,
}

export const birthControlEfficacy: Partial<
  Record<BirthControlMethodBreakpointOption, BirthControlMethodEfficacy>
> = {
  [BirthControlMethodBreakpointOption.pill]: {
    typicalUse: '93%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.condom]: {
    typicalUse: '87%',
    perfectUse: '98%',
  },
  [BirthControlMethodBreakpointOption.shot]: {
    typicalUse: '96%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.nuvaRing]: {
    typicalUse: '93%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.implant]: {
    typicalUse: '>99%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.hormonalIUD]: {
    typicalUse: '>99%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.pullOut]: {
    typicalUse: '80%',
    perfectUse: '96%',
  },
  [BirthControlMethodBreakpointOption.copperIUD]: {
    typicalUse: '>99%',
    perfectUse: '>99%',
  },
  [BirthControlMethodBreakpointOption.patch]: {
    typicalUse: '93%',
    perfectUse: '>99%',
  },
}

export const birthControlMethodInfoContent: Record<
  BirthControlMethodBreakpointOption,
  InfoPageContent
> = {
  [BirthControlMethodBreakpointOption.pill]: { titleKey: 'txt-hormone-free-zero-side-effects' },
  [BirthControlMethodBreakpointOption.condom]: { titleKey: 'txt-switch-and-use-condoms' },
  [BirthControlMethodBreakpointOption.shot]: { titleKey: 'txt-hormone-free-zero-side-effects' },
  [BirthControlMethodBreakpointOption.nuvaRing]: { titleKey: 'txt-hormone-free-zero-side-effects' },
  [BirthControlMethodBreakpointOption.implant]: { titleKey: 'txt-hormone-free-zero-side-effects' },
  [BirthControlMethodBreakpointOption.hormonalIUD]: {
    titleKey: 'txt-hormone-free-zero-side-effects',
  },
  [BirthControlMethodBreakpointOption.pullOut]: { titleKey: 'txt-switch-and-know-when-fertile' },
  [BirthControlMethodBreakpointOption.copperIUD]: { titleKey: 'txt-non-invasive-no-side-effects' },
  [BirthControlMethodBreakpointOption.other]: { titleKey: 'txt-switch-and-know-when-fertile' },
  [BirthControlMethodBreakpointOption.patch]: { titleKey: 'txt-hormone-free-zero-side-effects' },
  [BirthControlMethodBreakpointOption.none]: { titleKey: 'txt-switch-and-know-when-fertile' },
}

export const goalBreakpointOptionToGoal: Record<GoalBreakpointOption, Goal> = {
  [GoalBreakpointOption.prevent]: Goal.PREVENT,
  [GoalBreakpointOption.preventNowPlanLater]: Goal.PREVENT,

  [GoalBreakpointOption.manageSymptoms]: Goal.PREVENT,
  [GoalBreakpointOption.undecided]: Goal.PREVENT,
  [GoalBreakpointOption.trackCycle]: Goal.PREVENT,
  [GoalBreakpointOption.improveWellbeing]: Goal.PREVENT,

  [GoalBreakpointOption.plan]: Goal.PLAN,
  [GoalBreakpointOption.trackPregnancy]: Goal.PREGNANT,
  [GoalBreakpointOption.postPartum]: Goal.POSTPARTUM,
}

export const cycleChangesStoreToOptions: Partial<
  Record<
    QuizDataKey,
    {
      name: string
      en: AnyEnum
    }
  >
> = {
  emotions: { name: 'EmotionsOption', en: EmotionsOption },
  typeOfPain: { name: 'TypeOfPainOption', en: TypeOfPainOption },
  skinChanges: { name: 'SkinChangesOption', en: SkinChangesOption },
  libidoChanges: { name: 'LibidoChangesOption', en: LibidoChangesOption },
  bleedingChanges: { name: 'BleedingChangesOption', en: BleedingChangesOption },
  cervicalMucusChanges: { name: 'CervicalMucusChangesOption', en: CervicalMucusChangesOption },
}

export const cBguardQuizData: QuizGuardData<Page, QuizDataInput> = {
  [Page.fertilityGoal]: () => {
    return true
  },
  [Page.privacy]: state => !!state?.fertilityGoal,
  [Page.name]: state => !!state?.fertilityGoal,
  [Page.usps]: state => {
    const isTest393Two =
      getBucket(getSignalStore().$experiment().assignments[Experiment.QUIZ_REVAMP_USPS]) === 'test2'
    return isTest393Two ? !!state?.fertilityGoal : !!state?.name
  },
  [Page.birthday]: state => {
    return !!state?.name
  },
  [Page.fertilityGoalInfo]: state => {
    return !!state?.ageRange
  },
  [Page.birthControlMethod]: state => {
    return !!state?.ageRange && state?.fertilityGoal !== GoalBreakpointOption.plan
  },
  [Page.birthControlMethodInfo]: state => {
    return !!state?.birthControlMethod
  },

  [Page.sideEffects]: () => true,
  [Page.sideEffectsInfo]: state => {
    return !!state?.sideEffects
  },
  [Page.plannedPregnancyTiming]: state => {
    return !!state?.ageRange && state?.fertilityGoal === GoalBreakpointOption.plan
  },
  [Page.hormonalBirthControlUsage]: state => {
    return !!state?.knowPregnantDays
  },
  [Page.learnAboutSex]: state => {
    return !!state?.conceiveTiming
  },
  [Page.bestTimeInCycle]: state => {
    return !!state?.conceiveTiming
  },
  [Page.knowingYourSexDrive]: state => {
    return !!state?.bestTimeInCycle
  },
  [Page.sexDriveInfo]: state => {
    return !!state?.knowingYourSexDrive
  },
  [Page.sexFrequency]: state => {
    return !!state?.knowingYourSexDrive
  },
  [Page.knowPregnantDays]: state => {
    return !!state?.sexFrequency
  },
  [Page.menstrualCycleInfo]: state => {
    return !!state?.sexFrequency
  },
  [Page.testimonialInfo]: state => {
    return !!state?.sexFrequency
  },

  [Page.hormonalBirthControlHowLongPlan]: state => {
    return state?.hormonalBirthControlUsage === HormonalBirthControlUsageBreakpointOption.lastMonth
  },
  [Page.hormonalBirthControlHowLongPrevent]: state => {
    return !!state?.birthControlMethod
  },
  [Page.plannedPregnancyInfo]: state => {
    // This page appears in many flows. To reduce complexity and increase readability we decided to use the last "common" question as the guard.
    // Discussion here: https://github.com/NaturalCycles/WebSignup/pull/1497/files#r1231149370
    return !!state?.conceiveTiming
  },
  [Page.cycleLength]: state => {
    // This page appears in many flows. To reduce complexity and increase readability we decided to use a simplified logic for the route guard.
    // Discussion here https://github.com/NaturalCycles/WebSignup/pull/1497/files#r1231149370
    return !!state?.birthControlMethod || !!state?.conceiveTiming
  },
  [Page.periodRegularity]: state => {
    return !!state?.avgCycleLength
  },
  [Page.periodRegularityInfo]: state => {
    return (
      !!state?.periodRegularity ||
      state?.hormonalBirthControlHowLong ===
        HormonalBirthControlHowLongBreakpointOption.moreThanAYear
    )
  },
  [Page.cycleStartDate]: state => {
    return !!state?.periodRegularity
  },
  [Page.medicalConditionsPlan]: state => {
    return !!state?.conceiveTiming
  },
  [Page.medicalConditionsPrevent]: state => {
    return (
      (!!state?.periodRegularity && state.fertilityGoal !== GoalBreakpointOption.plan) ||
      state?.hormonalBirthControlHowLong ===
        HormonalBirthControlHowLongBreakpointOption.moreThanAYear
    )
  },
  [Page.medicalConditionsInfo]: state => {
    return !!state?.medicalConditions
  },
  [Page.doctorInvolvement]: state => {
    return !!state?.medicalConditions && state.fertilityGoal === GoalBreakpointOption.plan
  },
  [Page.partnerInvolvement]: state => {
    return !!state?.discussedFertility
  },
  [Page.partnerInvolvementInfo]: state => {
    return !!state?.partnerInvolvement
  },
  [Page.followPregnancyInfo]: state => {
    return !!state?.partnerInvolvement
  },
  [Page.emotionalChanges]: () => true,
  [Page.cycleChanges]: state => {
    return (
      (state?.fertilityGoal !== GoalBreakpointOption.plan && !!state?.medicalConditions) ||
      (state?.fertilityGoal === GoalBreakpointOption.plan && !!state?.partnerInvolvement)
    )
  },
  [Page.cycleChangesEmotions]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.emotions)
  },
  [Page.cycleChangesBleeding]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.bleeding)
  },
  [Page.cycleChangesSkin]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.skinChanges)
  },
  [Page.cycleChangesSexAndLibido]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.sexAndLibido)
  },
  [Page.cycleChangesCervicalMucus]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.cervicalMucus)
  },
  [Page.cycleChangesPain]: state => {
    return !!state?.cycleChanges?.includes(CycleChangesBreakpointOption.pain)
  },
  [Page.trackingInfo]: state => {
    /*
    NOTES:
    1) This rule does not comprise all possible combinations of ".cycleChanges" + their pages (Page.cycleChangesEmotions, Page.cycleChangesSkin, etc).
    2) Possible values of state?.cycleChanges are:
          - undefined // evaluates to false, user has never seen the page
          - [] // evaluates to true, user has seen the page, but skipped
          - [CycleChangesBreakpointOption.emotions, CycleChangesBreakpointOption.bleeding, ...] // evaluates to true, user has seen the page and selected some options
    */
    return !!state?.cycleChanges
  },
}

export const guardQuizData: QuizGuardData<Page, QuizDataInput> = {
  ...cBguardQuizData,
  [Page.naturalMethod]: state => {
    return !!state?.cycleChanges
  },
  [Page.yourMeasuringDevice]: state => {
    return !!state?.cycleChanges
  },

  [Page.whichWearable]: state => {
    return !!state?.cycleChanges
  },
  [Page.eligibleAppleWatch]: state => {
    return !!state?.whichWearable
  },
  [Page.purchaseNewWatch]: state => {
    return state?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.no
  },
  [Page.equallyEffectiveInfo]: state => {
    return !!state?.whichWearable
  },
  [Page.nightSleep]: state => {
    return !!state?.whichWearable
  },
  [Page.troubleGettingBackToSleep]: state => {
    return !!state?.whichWearable
  },
  [Page.sleepConsistency]: state => {
    return !!state?.sleepWithTracker
  },
  [Page.spendingDaysInfo]: state => {
    return !!state?.sleepConsistency
  },
  [Page.healthGoals]: state => {
    return !!state?.sleepConsistency
  },
  [Page.feelingStressed]: state => {
    return !!state?.healthGoals
  },
  [Page.biggestStressor]: state => {
    return !!state?.feelingStressed
  },
  [Page.favoriteWayToUnwind]: state => {
    return !!state?.feelingStressed
  },
  [Page.deviceTrait]: state => {
    return !!state?.favoriteWayToUnwind
  },
  [Page.sleepWearable]: state => {
    return !!state?.sleep || !!state?.whichWearable
  },
  [Page.wearYourWatchToBed]: state => {
    return !!state?.sleepWithTracker
  },
  [Page.fitResult]: state => {
    return !!state?.statementDescription
  },
  [Page.deviceResult]: state => {
    return (
      !!state?.whichWearable &&
      flowUtils.isLoggedIn({ state: getState(), params: new URL(location.href).searchParams })
    )
  },
}
